import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getAuditLogs, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { KernelPage } from '../kernelAdminShared';
import { diffObjects } from '../utils/diff';
import { fmtDateTime } from '../utils/kernelFormat';

const ACTIONS = ['create', 'update', 'delete'];
const ACTION_KEY = { create: 'audit.actionCreate', update: 'audit.actionUpdate', delete: 'audit.actionDelete' };
const DEFAULT_IGNORE_FIELDS = ['created_at', 'updated_at', 'user_id', 'company_id', 'id'];
const RECORD_VIEW_PER_PAGE = 100;

const valueToString = (v) => {
  if (v === undefined || v === null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/**
 * Fabbrica della pagina "Registro modifiche". Il modulo passa l'elenco delle
 * entità tracciate (vedi pages/AuditLog.js) e, se serve, opzioni di dominio:
 * - `entityLabels`   { entità: etichetta } per filtro e colonna "Entità"
 * - `fieldLabels`    { campo: etichetta } per la tabella delle differenze
 * - `formatField`    (campo, valore) => testo, per date/importi/codici di dominio
 * - `ignoreFields`   campi tecnici da non mostrare nelle differenze
 * - `recordView`     con ?entity=..&id=.. mostra lo storico di un solo record (con "Indietro")
 */
export function createAuditLogPage(ENTITIES, options = {}) {
  const {
    entityLabels = {},
    fieldLabels = {},
    formatField = (key, value) => valueToString(value),
    ignoreFields = DEFAULT_IGNORE_FIELDS,
    recordView = false,
  } = options;
  const entityLabel = (e) => entityLabels[e] || e;

  return function AuditLog({ user, onLogout }) {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const recordEntity = recordView ? searchParams.get('entity') || '' : '';
    const recordId = recordView ? searchParams.get('id') || '' : '';
    const isRecordView = !!recordEntity && !!recordId;

    const [auditEnabled, setAuditEnabled] = useState(null); // null = unknown/loading
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const perPage = isRecordView ? RECORD_VIEW_PER_PAGE : 20;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState(null);

    const [filters, setFilters] = useState({ entity: '', action: '', search: '', from: '', to: '' });

    const fetchLogs = useCallback(async (pageNum = page) => {
      setLoading(true);
      setError('');
      try {
        const params = { page: pageNum, per_page: perPage };
        if (isRecordView) {
          params.entity = recordEntity;
          params.entity_id = recordId;
        } else {
          Object.keys(filters).forEach((k) => {
            if (filters[k]) params[k] = filters[k];
          });
        }
        const data = await getAuditLogs(params);
        setAuditEnabled(!!data.audit_enabled);
        setItems(data.items || []);
        setTotal(data.total || 0);
        setPage(data.page || 1);
      } catch (err) {
        setError(errorMessage(err, t('audit.errorLoad')));
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, perPage, isRecordView, recordEntity, recordId]);

    useEffect(() => {
      fetchLogs(1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, isRecordView, recordEntity, recordId]);

    const setFilter = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const totalPages = Math.max(1, Math.ceil(total / perPage));
    const columns = isRecordView ? 5 : 6;

    return (
      <KernelPage user={user} onLogout={onLogout}>
        <div className="page-header">
          <div>
            {isRecordView && (
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => navigate(-1)}>
                ← {t('audit.back')}
              </button>
            )}
            <h1 className="page-title">
              {isRecordView ? `${t('audit.title')} — ${entityLabel(recordEntity)} #${recordId}` : t('audit.title')}
            </h1>
            <p className="page-subtitle">{t('audit.subtitle')}</p>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {auditEnabled === false && (
          <div className="warning-message">{t('audit.disabled')}</div>
        )}

        {auditEnabled !== false && (
          <div className="card">
            <div className="card-body">
              {!isRecordView && (
                <div className="audit-filters">
                  <div className="form-group">
                    <label>{t('audit.entity')}</label>
                    <select value={filters.entity} onChange={(e) => setFilter('entity', e.target.value)}>
                      <option value="">{t('audit.all')}</option>
                      {ENTITIES.map((e) => (
                        <option key={e} value={e}>
                          {entityLabel(e)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('audit.action')}</label>
                    <select value={filters.action} onChange={(e) => setFilter('action', e.target.value)}>
                      <option value="">{t('audit.all')}</option>
                      {ACTIONS.map((a) => (
                        <option key={a} value={a}>
                          {t(ACTION_KEY[a])}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('audit.search')}</label>
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilter('search', e.target.value)}
                      placeholder={t('audit.searchPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('audit.from')}</label>
                    <input type="date" value={filters.from} onChange={(e) => setFilter('from', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>{t('audit.to')}</label>
                    <input type="date" value={filters.to} onChange={(e) => setFilter('to', e.target.value)} />
                  </div>
                </div>
              )}

              {loading && <div className="loading">{t('common.loading')}</div>}

              {!loading && (
                <>
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t('audit.date')}</th>
                          <th>{t('audit.user')}</th>
                          <th>{t('audit.action')}</th>
                          {!isRecordView && <th>{t('audit.entity')}</th>}
                          <th>{t('audit.summary')}</th>
                          <th className="actions">{t('audit.details')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => {
                          const changes = diffObjects(item.before_data, item.after_data).filter(
                            (c) => !ignoreFields.includes(c.key)
                          );
                          return (
                            <React.Fragment key={item.id}>
                              <tr>
                                <td className="nowrap">{fmtDateTime(item.created_at)}</td>
                                <td>{item.user_name || '—'}</td>
                                <td>
                                  <span className={`badge badge-action badge-action-${item.action}`}>
                                    {ACTION_KEY[item.action] ? t(ACTION_KEY[item.action]) : item.action}
                                  </span>
                                </td>
                                {!isRecordView && (
                                  <td>
                                    {entityLabel(item.entity)}
                                    {item.entity_id != null && ` #${item.entity_id}`}
                                  </td>
                                )}
                                <td>{item.summary || '—'}</td>
                                <td className="actions">
                                  {changes.length > 0 && (
                                    <button
                                      type="button"
                                      className="icon-btn"
                                      title={t('audit.details')}
                                      onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                                    >
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6 9 12 15 18 9" />
                                      </svg>
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {expanded === item.id && (
                                <tr className="audit-diff-row">
                                  <td colSpan={columns}>
                                    <table className="table">
                                      <thead>
                                        <tr>
                                          <th>{t('audit.field')}</th>
                                          <th>{t('audit.before')}</th>
                                          <th>{t('audit.after')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {changes.map((c) => (
                                          <tr key={c.key}>
                                            <td>{fieldLabels[c.key] || <code>{c.key}</code>}</td>
                                            <td>{formatField(c.key, c.old)}</td>
                                            <td>{formatField(c.key, c.new)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {item.ip && <div className="audit-ip muted">{t('audit.ip')}: {item.ip}</div>}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {items.length === 0 && (
                          <tr>
                            <td colSpan={columns} className="empty-cell">
                              {isRecordView ? t('audit.noHistory') : t('audit.empty')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        disabled={page <= 1}
                        onClick={() => fetchLogs(page - 1)}
                      >
                        {t('audit.prev')}
                      </button>
                      <span className="pagination-info">
                        {t('audit.pageInfo', { page, total: totalPages })}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        disabled={page >= totalPages}
                        onClick={() => fetchLogs(page + 1)}
                      >
                        {t('audit.next')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </KernelPage>
    );
  };
}
